'use client'

import dynamic from 'next/dynamic'
import { type FC, type ReactNode } from 'react'

import { AlgoliaConfigContextProvider } from '../../../algolia'
import { ErrorBoundary } from '../../../error-boundary'
import { ExitIntentPopupModal } from '../../../experiments/exit-intent-popup-experiment'
import { NewsletterModalExperiment } from '../../../experiments/newsletter-modal-experiment/NewsletterModalExperiment'
import { useGlobalConfigContext } from '../../../global-config'
import { SHOP_ENV } from '../../../tenant'
import { useWebviewContext } from '../../../webview'
import { AlternativeUrlsContextProvider } from '../../alternativeUrls'
import { Footer } from '../../footer/components/Footer'
import { Header } from '../../header/components/Header'
import { HeaderSearchContextProvider } from '../../header/header-search/headerSearchContext'
import { type StandardLayoutProps } from '../../StandardLayoutProps'
import { StickyBannerContextProvider } from '../../stickyBanner/StickyBannerContext'

import { ButtonScrollToTop } from './ButtonScrollToTop/ButtonScrollToTop'

const RedcareToolbar = dynamic(
  async () =>
    import('../../../redcare-toolbar').then((mod) => mod.RedcareToolbar),
  {
    ssr: false,
  },
)

const StickyBanner = dynamic(
  async () =>
    import('../../stickyBanner/StickyBanner').then((mod) => mod.StickyBanner),
  {
    ssr: false,
  },
)

const ErxModal = dynamic(
  async () => import('../ErxModal/ErxModal').then((mod) => mod.ErxModal),
  {
    ssr: false,
  },
)

type StandardShopLayoutProps = {
  children?: Awaited<ReactNode>
  showStandardLayoutFooter?: boolean
  standardLayoutData: StandardLayoutProps
}

export const StandardShopLayout: FC<StandardShopLayoutProps> = ({
  children,
  showStandardLayoutFooter = true,
  standardLayoutData,
}) => {
  const isWebview = useWebviewContext()

  const { shopEnv } = useGlobalConfigContext()

  return (
    <AlternativeUrlsContextProvider
      alternativeUrls={standardLayoutData.alternativeUrls}
    >
      <AlgoliaConfigContextProvider
        algoliaConfig={standardLayoutData.algoliaConfig}
      >
        <StickyBannerContextProvider>
          {/* we need to keep providers that are required to be accessed from pages and hide just the header specific ones with layout */}
          {isWebview ? (
            children
          ) : (
            <main>
              <HeaderSearchContextProvider>
                <Header headerData={standardLayoutData.headerData} />
                <ExitIntentPopupModal
                  exitIntentPopupModalConfig={
                    standardLayoutData.exitIntentPopupModalConfig
                  }
                />
                <NewsletterModalExperiment />
                <ErxModal />
                {children}
              </HeaderSearchContextProvider>
              {showStandardLayoutFooter ? (
                <Footer footerData={standardLayoutData.footerData} />
              ) : null}
              <ButtonScrollToTop />
              <ErrorBoundary>
                <StickyBanner bannerData={standardLayoutData.headerData} />
              </ErrorBoundary>
            </main>
          )}
        </StickyBannerContextProvider>
        {shopEnv !== SHOP_ENV.PROD && <RedcareToolbar />}
      </AlgoliaConfigContextProvider>
    </AlternativeUrlsContextProvider>
  )
}
